import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Address } from '../../../../api/app-types/address.app';
import { ContactPoint, ContactPointCategory } from '../../../../api/app-types/contact-point.app';
import { Institution } from '../../../../api/app-types/institution.app';
import { RoleId } from '../../../../api/app-types/role.app';
import { User } from '../../../../api/app-types/user.app';
import { CreateWorkplace } from '../../../../api/app-types/workplace.app';
import { WORKPLACE_SERVICE, WorkplaceMockApi } from '../../../../api/services/workplace.api';
import { AddressCompletion } from '../../../../core/components/address-completion/address-completion';
import { BranchField } from '../../../../core/components/branch-field/branch-field';
import { LvrContactPoint } from '../../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { compareById } from '../../../../shared/helpers/compare-by-id.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { RouteInjection } from '../../../../shared/routing/route-injection.class';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { addressRequired } from '../../../../shared/validators/address-required-validator';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { isContactPointComplete } from '../../../../shared/helpers/is-contact-point-complete.func';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';

@Component({
	selector: 'lvr-workplace-create',
	imports: [
		MatFormFieldModule,
		MatSelectModule,
		MatTooltipModule,
		TranslocoModule,
		ReactiveFormsModule,
		LvrAppendAsteriskPipe,
		MatDatepickerModule,
		MatRadioModule,
		MatIconModule,
		MatButtonModule,
		MatInputModule,
		LvrInputDirective,
		LvrAutoSaveDirective,
		AddressCompletion,
		LvrPaddingDirective,
		LvrRestoreActions,
		LvrField,
		LvrContactPoint,
		BranchField,
		LvrDefaultActions,
		LvrButton,
	],
	templateUrl: './workplace-create.html',
	styleUrl: './workplace-create.scss',
	providers: [
		WorkplaceMockApi,
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
})
export class WorkplaceCreate extends RouteInjection {
	private readonly workplaceApi = inject(WORKPLACE_SERVICE);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly notificationService = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	public readonly compareById = compareById;
	public readonly hasError = hasError;
	public readonly draftId = FORM_KEYS.CREATE_INSTITUTION_WORKPLACE;
	public readonly environment = inject(ENVIRONMENT);
	private readonly branchRegex = new RegExp(this.environment.branchNameRegexStr);
	public readonly newWorkplaceForm = this.initNewWorkplaceForm(inject(FormBuilder));
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly employmentRequired = signal(true);
	public readonly employmentNotRequired = computed(() => !this.employmentRequired());

	private readonly data = toSignal(this.route.data);
	public readonly institution = computed(() => this.data()!['institution'] as Institution);
	public readonly user = computed(() => this.data()!['user'] as User);
	public readonly eaaRole = RoleId.EAA;
	public readonly saving = signal(false);
	private readonly contactPointCategoriesData =
		this.resolveDataAsSignal<PaginatedResponse<ContactPointCategory>>('contactPointCategories');
	public readonly contactPointCategories = computed<ContactPointCategory[]>(
		() => this.contactPointCategoriesData()?.entities ?? [],
	);
	public readonly communicationPointCount = signal(1);
	public readonly communicationPointCountArrayKeys = computed(() => Array(this.communicationPointCount()));

	public readonly canEditInstitutionsPermission = hasPermission(this.session.permissions, 'Institutions.Edit');

	public addContactPoint() {
		this.communicationPointCount.set(this.communicationPointCount() + 1);
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.newWorkplaceForm.controls.contactPoints;
		if (!ctrl) return;
		const values = (ctrl.value ?? []) as ContactPoint[];
		if (data.valid) {
			if (index >= 0 && index < values.length) {
				values[index] = data.value;
			} else {
				values.push(data.value);
			}
		} else {
			if (index >= 0 && index < values.length) {
				values.splice(index, 1);
			}
		}

		ctrl.setValue([...values]);
	}

	public removeContactPoint(id: number) {
		const ctrl = this.newWorkplaceForm.controls.contactPoints;
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		const index = values.findIndex((v) => v.id === id);
		if (index !== -1) {
			values.splice(index, 1);
			ctrl.setValue(values);
		}
		this.communicationPointCount.set(this.communicationPointCount() - 1);
	}

	/**
	 * Cancel new workplace creation.
	 */
	public cancel(): void {
		this.draftService.deleteDraft('institution-workplace', this.draftId);
		this.router.navigate(['..'], { relativeTo: this.route });
	}

	/**
	 * if checkbox is changed
	 */
	public changeEmploymentNotRequired(checked: boolean) {
		this.employmentRequired.set(!checked);
	}

	/**
	 * Create a new Workplace.
	 */
	public createWorkplace = async () => {
		if (this.saving()) return;

		if (this.newWorkplaceForm.invalid) {
			this.newWorkplaceForm.markAllAsTouched();
			return;
		}

		const EMPTY_ADDRESS = {
			streetAndHouseNumber: '',
			postalCodeAndCity: '',
		};

		const formValue = this.newWorkplaceForm.value;

		const body: CreateWorkplace = {
			name: formValue.name?.trim() ?? '',
			address:
				formValue.address === undefined || formValue.address === null ? (EMPTY_ADDRESS as Address) : formValue.address,
			contactPoints: [],
			industryName: formValue.industryName?.trim(),
		};

		const filteredCps = (formValue.contactPoints ?? []).filter(isContactPointComplete);

		if (filteredCps.length > 0) {
			const additionalCps = filteredCps.map((cp) => ({
				preferredWayOfContact: cp.preferredWayOfContact,
				categoryContactPointId: cp.categoryContactPointId,
				contactValue: cp.contactValue?.trim(),
				remark: cp.remark?.trim(),
			}));

			body.contactPoints.push(...additionalCps);
		}

		this.saving.set(true);

		try {
			await this.workplaceApi.create(body, this.institution().id);
			this.notificationService.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.workplace_created')),
			);
			this.draftService.deleteDraft('workplace', this.draftId);
			await this.router.navigate([`/desk/institution/${this.institution().id}/workplace`]);
		} catch (e) {
			console.error(e);
			this.notificationService.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.workplace_create_error')),
			);
		} finally {
			this.saving.set(false);
		}
	};

	/**
	 * Initializes the form for creating a new client.
	 */
	private initNewWorkplaceForm(fb: FormBuilder) {
		return fb.group({
			name: fb.control('', [Validators.required, Validators.pattern(this.branchRegex)]),
			address: fb.control<Address | undefined>(undefined, [addressRequired]),
			contactPoints: fb.control<ContactPoint[]>([], {
				validators: [Validators.maxLength(5)],
			}),
			industryName: fb.control(''),
		});
	}
}
